import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import SearchInputLight from "../../../inputs/SearchInputLight";
import { grey } from "@mui/material/colors";
import AssociationSelectorButton from "./AssociationSelectorButton";
import { isFunction } from "lodash";

const AssociationSelectorDropdown = ({
  config,
  onClick,
  disabled,
  options,
  associationIds,
  associationKey,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!options || options.length === 0) {
      if (isFunction(onClick)) {
        onClick();
      }
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchValue("");
  };

  const handleMenuItemClick = (option) => {
    if (isFunction(onClick)) {
      onClick(option._id);
    }
    setAnchorEl(null);
    setSearchValue("");
  };

  const filteredOptions = useMemo(() => {
    if (!options) return [];

    let filtered = options;

    if (associationIds && associationKey && associationIds[associationKey]) {
      filtered = filtered.filter(
        (option) => !associationIds[associationKey].includes(option._id)
      );
    }

    if (config.enableSearch && searchValue && searchValue.trim()) {
      const searchTerm = searchValue.toLowerCase().trim();
      filtered = filtered.filter((option) =>
        option.label.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [
    options,
    searchValue,
    config.enableSearch,
    associationIds,
    associationKey,
  ]);

  return (
    <>
      <AssociationSelectorButton
        tooltip={config.tooltip}
        icon={config.icon}
        onClick={handleClick}
        disabled={disabled}
      />

      {options && options.length > 0 && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          sx={{
            "& .MuiPopover-paper": {
              mt: 1,
              border: "1px solid #dde0e4",
              borderRadius: "4px",
              minWidth: "200px",
              maxWidth: "400px",
            },
          }}
        >
          <Box sx={{ width: "100%" }}>
            {config.enableSearch && (
              <Box
                sx={{
                  px: 1.4,
                  py: 1,
                  borderBottom: "1px solid #dde0e4",
                }}
              >
                <SearchInputLight
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder="Search..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      setSearchValue("");
                    }
                  }}
                />
              </Box>
            )}

            <List
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                py: 0.5,
              }}
              disablePadding
            >
              {filteredOptions.length === 0 ? (
                <ListItem
                  sx={{
                    pl: 1.4,
                    fontStyle: "italic",
                    color: grey[600],
                    cursor: "default",
                  }}
                >
                  <ListItemText
                    primary="No options available"
                    primaryTypographyProps={{ fontSize: "12px" }}
                  />
                </ListItem>
              ) : (
                filteredOptions.map((option, index) => (
                  <ListItem
                    key={option._id || index}
                    onClick={() => handleMenuItemClick(option)}
                    sx={{
                      pl: 1.4,
                      pr: 1.4,
                      py: 0.5,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: grey[100],
                      },
                      ".MuiListItemIcon-root": {
                        minWidth: "26px",
                        ".MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                      },
                    }}
                  >
                    {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{ fontSize: "11px" }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default AssociationSelectorDropdown;
